<template>
  <rounded-card
    show-right-btn
    :head-text="headText"
    :padding="padding"
    :show-left-btn="canGoBack"
    @back-handler="goBack"
    @set-close-status="closeMenu"
  >
    <feedback v-if="activePage === 'feedback'" :sub-page.sync="subPage" @report="report" />
    <language v-else-if="activePage === 'language'" @set-locale="setLocale" />
    <account-settings
      v-else-if="activePage === 'user-setting'"
      :sub-page.sync="subPage"
      @delete-account="deleteAccount"
      @logout="logout"
    />
    <mtm-login
      v-else-if="activePage === 'mtm-login'"
      :mtm-user="myAccount"
      :mtm-user-confirmed="confirmedLoggedIn"
      :mtm-user-logged-in="hasMtmLoggedIn"
      :need-confirm="needConfirm"
      :error="mtmLoginError"
      @login="loginMtmUser"
      @confirm="confirmMtmUser"
      @logout="logoutMtmUser"
    />
    <menu-list v-else :sort-menu="sortMenu" @menu="showMenu" />
  </rounded-card>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import op from 'object-path';
import Storage from '@/modules/storage';
import AudioManager from '@/modules/audio-manager';
import ApiActions from '@/modules/api-actions';

import RoundedCard from '@/components/common/RoundedCard.vue';
import Feedback from '@/components/DefaultCloudzet/common/Feedback.vue';
import Language from '@/components/DefaultCloudzet/common/Language.vue';
import MenuList from './MenuList.vue';
import MtmLogin from './MtmLogin.vue';
import AccountSettings from './AccountSettings.vue';

export default {
  name: 'Menu',
  components: {
    RoundedCard,
    MenuList,
    MtmLogin,
    AccountSettings,
    Feedback,
    Language,
  },
  data: () => ({
    mtmLoginError: '',
    routes: [],
    subPage: '',
  }),
  methods: {
    ...mapActions('app', ['hideMenu', 'showMenu', 'setAppMode']),
    ...mapActions('mtmUser', ['setConfirmedLoggedIn']),
    closeMenu() {
      this.subPage = '';
      this.routes.splice(0);
      this.hideMenu();
    },
    goBack() {
      if (this.subPage) {
        this.subPage = '';
      } else {
        this.showMenu();
      }
    },
    async loginMtmUser(data) {
      this.$events.startLoading();
      try {
        await ApiActions.mtm.login(data);
        this.$emit('mtm-login-done');
        this.closeMenu();
      } catch (e) {
        ApiActions.mtm.logout();
        const { errno, msg } = op.get(e, 'response.data.error', {});
        if (msg) {
          this.mtmLoginError = msg;
          console.log('mtm login failed', errno, msg);
        }
        this.$events.stopLoading();
      }
    },
    async confirmMtmUser(data) {
      this.$events.startLoading();
      try {
        await ApiActions.mtm.confirmLogin(data);
        this.setConfirmedLoggedIn(true);
      } catch (e) {
        const { errno, msg } = op.get(e, 'response.data.error', {});
        if (msg) {
          this.mtmLoginError = msg;
          console.log('mtm login failed', errno, msg);
        }
      } finally {
        this.$events.stopLoading();
      }
    },
    logoutMtmUser() {
      if (this.isStamp) {
        AudioManager.unloadAudio();
      }
      ApiActions.mtm.logout();
      this.$events.goto('Login');
      this.closeMenu();
    },
    logout() {
      ApiActions.user.logout();
      this.$events.goto('Login');
      this.closeMenu();
    },
    report(content) {
      // this will do
      console.log('[ERROR]', content);
    },
    async deleteAccount() {
      this.$events.startLoading();
      try {
        await ApiActions.user.deleteAccount();
        this.logout();
      } catch (e) {
        console.log(e.message);
      } finally {
        this.$events.stopLoading();
      }
    },
    setLocale(language) {
      this.$events.startLoading();
      const locale = language.value;
      this.$root.$i18n.locale = locale;
      Storage.setItem('locale', locale);
      if (this.isContactless) {
        this.$events.stopLoading();
        return;
      }

      this.setAppMode(locale);
      const done = () => {
        this.goBack();
        this.$events.stopLoading();
      };
      AudioManager.loadAllAudio(locale).then(done, done);
    },
  },
  computed: {
    ...mapState('app', ['appReady', 'menuPage']),
    ...mapState('mtmUser', {
      mtmMe: state => state.me,
      confirmedLoggedIn: 'confirmedLoggedIn',
    }),
    ...mapGetters('app', ['isStamp']),
    ...mapGetters('user', ['isAuthenticated']),
    ...mapGetters('mtmUser', {
      isContactless: 'isContactless',
      myAccount: 'myAccount',
      mtmIsAuthenticated: 'isAuthenticated',
    }),

    activePage() {
      const { pageName } = this.menuPage || {};
      return pageName || '';
    },
    directOpen() {
      const { directOpen = false } = this.menuPage || {};
      return directOpen || false;
    },
    hasMtmLoggedIn() {
      return !!this.myAccount;
    },
    headText() {
      const { pageName } = this.menuPage || {};
      let i18nKey;
      switch (pageName) {
        case 'avatar-shader':
          i18nKey = 'homePage.avatar_shader';
          break;
        case 'language':
          i18nKey = 'homePage.language';
          break;
        case 'feedback':
          i18nKey = 'homePage.feedback';
          break;
        case 'user-setting':
          i18nKey = 'homePage.account_settings';
          break;
        case 'mtm-login': {
          if (!this.mtmIsAuthenticated) {
            i18nKey = 'homePage.login_s4f';
          } else {
            i18nKey = this.needConfirm ? 'login.enter_password' : 'homePage.store_settings';
          }
          break;
        }
        default:
          i18nKey = 'homePage.menu';
      }
      return this.$t(i18nKey);
    },
    padding() {
      return this.activePage === 'mtm-login' && !this.hasMtmLoggedIn;
    },
    sortMenu() {
      const common = [
        // 'avatar-shader', TODO: implement this
        'language',
        'get-help',
      ];
      const authed = [];
      if (this.isAuthenticated) {
        authed.push('user-setting');
      } else {
        authed.push('mtm-login');
      }
      return [...common, ...authed];
    },
    canGoBack() {
      return this.routes.length > 0 && !this.directOpen;
    },
    needConfirm() {
      return this.hasMtmLoggedIn && !this.confirmedLoggedIn;
    },
  },
  watch: {
    activePage(nv) {
      this.mtmLoginError = '';
      if (nv) {
        this.routes.push(nv);
      } else {
        this.routes.pop();
        this.subPage = '';
      }
    },
  },
};
</script>
